import React, { useEffect } from 'react';
import {
  Box,
  Container,
  Heading,
  Text,
  VStack,
  HStack,
  Grid,
  GridItem,
  Button,
  Icon,
  Flex,
  Link,
  useToast,
} from '@chakra-ui/react';
import { 
  FaPhone,
  FaEnvelope,
  FaMapMarkerAlt,
  FaClock,
  FaExclamationTriangle,
  FaComments,
  FaCheckCircle
} from 'react-icons/fa';
import FacilityMap from '../components/map/FacilityMap';
import PhysicianPartnershipForm from '../components/forms/PhysicianPartnershipForm';
import { Reveal, RevealGroup, RevealItem } from '../components/motion';
import { PageHero, SectionHeading, IconMark, LightSheet } from '../components/premium';

// One card surface for the whole page: white, a hairline edge, the soft
// two-layer shadow, and a slow settle on hover.
const HAIRLINE = 'rgba(14,47,58,0.06)';
const card = {
  bg: 'white',
  borderRadius: '24px',
  boxShadow: 'card',
  border: '1px solid',
  borderColor: HAIRLINE,
};
const cardHover = {
  transition: 'transform 0.5s cubic-bezier(0.16,1,0.3,1)',
  _hover: { transform: 'translateY(-3px)' },
};

// Small tracked label used for PHONE / EMAIL / ADDRESS / HOURS.
const labelProps = {
  size: 'sm',
  color: 'ink.900',
  fontWeight: '600',
  letterSpacing: '0.14em',
  fontSize: 'xs',
};

const Contact = () => {
  const toast = useToast();

  // Function to programmatically open Podium chat
  const openPodiumChat = () => {
    // Check if Podium widget is loaded
    if (window.podium && window.podium.widget) {
      window.podium.widget.open();
    } else {
      // If Podium isn't loaded yet, show a message
      toast({
        title: 'Chat Loading',
        description: 'The chat widget is loading. It should appear in the bottom right corner of your screen.',
        status: 'info',
        duration: 5000,
        isClosable: true,
      });
      
      // Try to trigger Podium after a delay
      setTimeout(() => {
        if (window.podium && window.podium.widget) {
          window.podium.widget.open();
        }
      }, 1500);
    }
  };

  // Check for Podium on mount and add a slight delay
  useEffect(() => {
    // Log Podium status for debugging
    setTimeout(() => {
      if (window.podium) {
        console.log('Podium is available on Contact page');
      }
    }, 2000);
  }, []);

  return (
    <Box>
      {/* Hero Section */}
      {/* Photographic hero, shared with every inner page. See
          components/premium/PageHero for why the photo is no longer
          hidden under a cyan gradient. */}
      <PageHero
        title={"Contact Us"}
        subtitle={"We're here to help. Chat with us or call us directly."}
        image="/facility/exterior.jpg"
        imagePosition="center 70%"
      />

      {/* Emergency Notice */}
      {/* Deliberately not animated: the 911 notice is on screen from the
          first frame. Calm but unmistakable, a white card with a restrained
          red edge rather than a saturated block. */}
      <Container maxW="container.xl" mt={-10} position="relative" zIndex={2}>
        <Box
          bg="white"
          border="1px solid"
          borderColor="red.200"
          borderLeft="4px solid"
          borderLeftColor="red.600"
          borderRadius="24px"
          p={{ base: 6, md: 7 }}
          boxShadow="card"
        >
          <HStack spacing={5} align="start">
            <Flex
              w="52px"
              h="52px"
              flexShrink={0}
              borderRadius="full"
              align="center"
              justify="center"
              bg="red.50"
              border="1px solid"
              borderColor="red.200"
            >
              <Icon as={FaExclamationTriangle} boxSize={5} color="red.600" />
            </Flex>
            <VStack align="start" spacing={2}>
              <Heading size="md" color="red.700" fontWeight="600">
                For emergent issues call 911
              </Heading>
              <Text color="ink.700" fontSize="sm">
                This chat service is not monitored for medical emergencies. If you are experiencing a medical emergency, please call 911 immediately.
              </Text>
            </VStack>
          </HStack>
        </Box>
      </Container>

      {/* Contact Information & Chat Section */}
      <Container maxW="container.xl" py={20}>
        <Grid templateColumns={{ base: '1fr', lg: '1fr 1.5fr' }} gap={12}>
          {/* Contact Information */}
          <GridItem>
            <RevealGroup display="flex" flexDirection="column" gap={6}>
              {/* Phone */}
              <RevealItem>
                <Box {...card} {...cardHover} p={6}>
                  <HStack spacing={4} mb={4}>
                    <IconMark icon={FaPhone} size="sm" />
                    <Heading {...labelProps}>
                      PHONE
                    </Heading>
                  </HStack>
                  <Link
                    href="tel:+13213213935"
                    fontSize="xl"
                    fontWeight="600"
                    color="ink.900"
                    _hover={{ color: 'brand.700' }}
                  >
                    (321) 321-3935
                  </Link>
                  <Text fontSize="sm" color="brand.700" mt={2} fontWeight="600">
                    Call Now
                  </Text>
                </Box>
              </RevealItem>

              {/* Email */}
              <RevealItem>
                <Box {...card} {...cardHover} p={6}>
                  <HStack spacing={4} mb={4}>
                    <IconMark icon={FaEnvelope} size="sm" />
                    <Heading {...labelProps}>
                      EMAIL
                    </Heading>
                  </HStack>
                  <Link
                    href="mailto:info@lucienasc.com"
                    fontSize="lg"
                    fontWeight="600"
                    color="ink.900"
                    _hover={{ color: 'brand.700' }}
                  >
                    info@lucienasc.com
                  </Link>
                </Box>
              </RevealItem>

              {/* Address */}
              <RevealItem>
                <Box {...card} {...cardHover} p={6}>
                  <HStack spacing={4} mb={4}>
                    <IconMark icon={FaMapMarkerAlt} size="sm" />
                    <Heading {...labelProps}>
                      ADDRESS
                    </Heading>
                  </HStack>
                  <Text fontSize="md" color="ink.700" lineHeight="tall">
                    2200 LUCIEN WAY,<br />
                    SUITE 100, MAITLAND,<br />
                    FL - 32751
                  </Text>
                  <Link
                    href="https://maps.google.com/?q=2200+Lucien+Way+Suite+100+Maitland+FL+32751"
                    target="_blank"
                    rel="noopener noreferrer"
                    fontSize="sm"
                    color="brand.700"
                    mt={2}
                    fontWeight="600"
                    display="inline-block"
                    _hover={{ color: 'ink.900' }}
                  >
                    Get Directions →
                  </Link>
                </Box>
              </RevealItem>

              {/* Hours */}
              <RevealItem>
                <Box {...card} {...cardHover} p={6}>
                  <HStack spacing={4} mb={4}>
                    <IconMark icon={FaClock} size="sm" />
                    <Heading {...labelProps}>
                      HOURS
                    </Heading>
                  </HStack>
                  <VStack align="start" spacing={3}>
                    <Box>
                      <Text fontSize="sm" color="ink.600" fontWeight="600" mb={1}>
                        Monday - Thursday
                      </Text>
                      <Text fontSize="lg" fontWeight="600" color="ink.900">
                        7:00 AM – 4:00 PM
                      </Text>
                    </Box>
                    <Box>
                      <Text fontSize="sm" color="ink.600" fontWeight="600" mb={1}>
                        Friday
                      </Text>
                      <Text fontSize="lg" fontWeight="600" color="ink.900">
                        7:00 AM – 4:00 PM
                      </Text>
                    </Box>
                    <Box>
                      <Text fontSize="sm" color="ink.600" fontWeight="600" mb={1}>
                        Saturday - Sunday
                      </Text>
                      <Text fontSize="md" fontWeight="600" color="ink.500">
                        Closed
                      </Text>
                    </Box>
                  </VStack>
                </Box>
              </RevealItem>
            </RevealGroup>
          </GridItem>

          {/* Chat Section - Replacing the Form */}
          <GridItem>
            <Reveal>
              <Box
                bg="mist.50"
                p={{ base: 6, md: 8 }}
                borderRadius="28px"
                border="1px solid"
                borderColor={HAIRLINE}
                boxShadow="cardSoft"
              >
                <VStack spacing={6} align="stretch">
                  <HStack spacing={4}>
                    <IconMark icon={FaComments} size="lg" bg="white" />
                    <Heading size="lg" color="ink.900" fontWeight="600">
                      Chat with Us
                    </Heading>
                  </HStack>

                  <Text color="ink.700" fontSize="lg">
                    Have a question? We're here to help! Click the button below to start a live chat with our team.
                  </Text>

                  <Box
                    bg="white"
                    p={6}
                    borderRadius="20px"
                    border="1px solid"
                    borderColor={HAIRLINE}
                  >
                    <VStack spacing={4} align="stretch">
                      <Text fontWeight="600" color="ink.900" fontSize="lg">
                        Instant Support Available
                      </Text>

                      <VStack align="start" spacing={3}>
                        <HStack>
                          <Icon as={FaCheckCircle} color="brand.600" />
                          <Text color="ink.700">Quick response times</Text>
                        </HStack>
                        <HStack>
                          <Icon as={FaCheckCircle} color="brand.600" />
                          <Text color="ink.700">Schedule appointments</Text>
                        </HStack>
                        <HStack>
                          <Icon as={FaCheckCircle} color="brand.600" />
                          <Text color="ink.700">General inquiries</Text>
                        </HStack>
                        <HStack>
                          <Icon as={FaCheckCircle} color="brand.600" />
                          <Text color="ink.700">Get directions & hours</Text>
                        </HStack>
                      </VStack>
                    </VStack>
                  </Box>

                  <Button
                    onClick={openPodiumChat}
                    variant="brandGradient"
                    size="lg"
                    fontSize="lg"
                    py={8}
                    leftIcon={<Icon as={FaComments} boxSize={6} />}
                  >
                    Start Live Chat
                  </Button>

                  <Text fontSize="sm" color="ink.600" textAlign="center" fontStyle="italic">
                    Chat available during business hours. Messages received after hours will be responded to the next business day.
                  </Text>

                  {/* Alternative Contact Methods */}
                  <Box
                    bg="white"
                    p={6}
                    borderRadius="20px"
                    border="1px solid"
                    borderColor={HAIRLINE}
                    mt={4}
                  >
                    <Text fontWeight="600" color="ink.900" mb={3}>
                      Other Ways to Reach Us:
                    </Text>
                    <VStack align="start" spacing={2}>
                      <HStack>
                        <Text color="ink.600">• Call us directly at</Text>
                        <Link href="tel:+13213213935" color="brand.700" fontWeight="600">
                          (321) 321-3935
                        </Link>
                      </HStack>
                      <HStack>
                        <Text color="ink.600">• Email us at</Text>
                        <Link href="mailto:info@lucienasc.com" color="brand.700" fontWeight="600">
                          info@lucienasc.com
                        </Link>
                      </HStack>
                    </VStack>
                  </Box>

                  {/* HIPAA Notice */}
                  <Box
                    bg="white"
                    p={4}
                    borderRadius="16px"
                    border="1px solid"
                    borderColor="brand.100"
                  >
                    <HStack spacing={2} align="start">
                      <Icon as={FaExclamationTriangle} color="brand.600" boxSize={4} mt={1} />
                      <Text fontSize="sm" color="ink.700">
                        <Text as="span" fontWeight="600" color="ink.900">Privacy Note:</Text> Please do not share personal health information via chat. 
                        For medical records or sensitive health matters, please call us directly.
                      </Text>
                    </HStack>
                  </Box>
                </VStack>
              </Box>
            </Reveal>

            {/* Additional Info Box */}
            <Reveal>
              <Box
                mt={6}
                p={6}
                {...card}
              >
                <Heading size="md" mb={4} color="ink.900" fontWeight="600">
                  What You Can Ask About
                </Heading>
                <Grid templateColumns={{ base: '1fr', md: '1fr 1fr' }} gap={4}>
                  <VStack align="start" spacing={2}>
                    <Text fontWeight="600" color="ink.900">General Inquiries</Text>
                    <Text fontSize="sm" color="ink.600">• Facility information</Text>
                    <Text fontSize="sm" color="ink.600">• Services offered</Text>
                    <Text fontSize="sm" color="ink.600">• Insurance questions</Text>
                  </VStack>
                  <VStack align="start" spacing={2}>
                    <Text fontWeight="600" color="ink.900">Scheduling</Text>
                    <Text fontSize="sm" color="ink.600">• Appointment availability</Text>
                    <Text fontSize="sm" color="ink.600">• Rescheduling</Text>
                    <Text fontSize="sm" color="ink.600">• Pre-visit requirements</Text>
                  </VStack>
                </Grid>
              </Box>
            </Reveal>
          </GridItem>
        </Grid>
      </Container>

      {/* Physician Partnership Form Section */}
      <Container maxW="container.xl" py={20} id="physician-partnerships">
        <PhysicianPartnershipForm />
      </Container>

      {/* Google Maps Section */}
      {/* Was a pale cyan wash; now the quiet mist surface, arriving as a
          sheet so the page closes on a settled note. */}
      <LightSheet tone="mist">
        <Container maxW="container.xl">
          <VStack spacing={8}>
            <SectionHeading title="Visit Our Facility" size="xl" mb={0} />

            <Reveal w="100%">
              <FacilityMap height="500px" />
            </Reveal>

            <Text fontSize="lg" color="ink.600" textAlign="center">
              2200 Lucien Way, Suite 100, Maitland, Florida - 32751
            </Text>
          </VStack>
        </Container>
      </LightSheet>
    </Box>
  );
};

export default Contact;
